import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import {
  FaBoxes,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaEdit,
  FaExclamationTriangle,
  FaFire,
  FaPlus,
  FaRedo,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaStar,
  FaTags,
  FaTimes,
  FaTimesCircle,
  FaTrash,
  FaUndo,
} from "react-icons/fa";
import "./AdminProducts.css";

const DEFAULTS = {
  q: "",
  category: "",
  stock: "",
  status: "",
  highlight: "",
  sort: "newest",
  page: 1,
  per_page: 25,
  deleted: false,
};

const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "price_desc", label: "Price high–low" },
  { value: "price_asc", label: "Price low–high" },
  { value: "stock_asc", label: "Stock low–high" },
  { value: "stock_desc", label: "Stock high–low" },
];

function readFilters(search) {
  const params = new URLSearchParams(search);
  const filters = { ...DEFAULTS };
  Object.keys(DEFAULTS).forEach((key) => {
    if (!params.has(key)) return;
    const value = params.get(key);
    if (key === "page" || key === "per_page") filters[key] = Math.max(1, Number(value) || DEFAULTS[key]);
    else if (key === "deleted") filters[key] = value === "1";
    else filters[key] = value;
  });
  return filters;
}

// Only values that differ from the defaults go into the address and the request.
function filtersToQuery(filters) {
  const params = new URLSearchParams();
  Object.keys(DEFAULTS).forEach((key) => {
    const value = filters[key];
    if (value === DEFAULTS[key] || value === "" || value === false) return;
    params.set(key, key === "deleted" ? "1" : String(value));
  });
  return params.toString();
}

// The server returns the paged list only when a page is given.
function listUrl(key) {
  const params = new URLSearchParams(key);
  if (!params.has("page")) params.set("page", "1");
  return `/api/view-products?${params.toString()}`;
}

function money(value) {
  return `Rs. ${Number(value || 0).toLocaleString()}`;
}

export default function ViewProduct() {
  const history = useHistory();
  const location = useLocation();
  const [filters, setFilters] = useState(() => readFilters(location.search));
  const [searchText, setSearchText] = useState(filters.q);
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState("");
  const [exporting, setExporting] = useState(false);
  const cache = useRef(new Map());
  const inflight = useRef(new Map());
  const busyRef = useRef("");

  const queryKey = filtersToQuery(filters);

  useEffect(() => {
    document.title = filters.deleted ? "Deleted Products — Admin" : "Products — Admin";
  }, [filters.deleted]);

  // Keep the address in step with the filters, so Back from Edit returns to the same view.
  useEffect(() => {
    const search = queryKey ? `?${queryKey}` : "";
    if (location.search !== search) history.replace({ pathname: location.pathname, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  // Search waits until typing pauses.
  useEffect(() => {
    if (searchText === filters.q) return undefined;
    const timer = setTimeout(() => {
      setFilters((current) => ({ ...current, q: searchText.trim(), page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, filters.q]);

  const fetchKey = useCallback((key) => {
    if (cache.current.has(key)) return Promise.resolve(cache.current.get(key));
    if (inflight.current.has(key)) return inflight.current.get(key);
    const request = axios.get(listUrl(key)).then((res) => {
      if (res.data.status !== 200) throw new Error(res.data.message || "The products could not be loaded.");
      cache.current.set(key, res.data);
      return res.data;
    }).finally(() => {
      inflight.current.delete(key);
    });
    inflight.current.set(key, request);
    return request;
  }, []);

  const load = useCallback((silent = false) => {
    let active = true;
    if (!silent) setFetching(true);
    setLoadError("");
    fetchKey(queryKey).then((result) => {
      if (!active) return;
      setData(result);
      setFetching(false);
      // Prefetch the next page so moving forward is instant.
      if (result.meta && result.meta.page < result.meta.page_count) {
        fetchKey(filtersToQuery({ ...filters, page: result.meta.page + 1 })).catch(() => {});
      }
    }).catch((error) => {
      if (!active) return;
      setFetching(false);
      setLoadError(error.response?.data?.message || error.message || "The products could not be loaded.");
    });
    return () => {
      active = false;
    };
  }, [fetchKey, queryKey, filters]);

  useEffect(() => load(), [load]);

  const refresh = useCallback(() => {
    cache.current.clear();
    load(true);
  }, [load]);

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value, page: key === "page" ? value : 1 }));
  };

  const goToPage = (page) => {
    setFilter("page", page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filterCategory = useCallback((id) => {
    setFilters((current) => ({ ...current, category: String(id || 0), page: 1 }));
  }, []);

  const resetFilters = () => {
    setSearchText("");
    setFilters((current) => ({ ...DEFAULTS, per_page: current.per_page, sort: current.sort, deleted: current.deleted }));
  };

  // Changes one row on screen right away, then reloads in the background so counts stay right.
  const patchRow = useCallback((id, changes) => {
    setData((current) => current && {
      ...current,
      products: current.products.map((row) => (row.id === id ? { ...row, ...changes } : row)),
    });
    refresh();
  }, [refresh]);

  const dropRow = useCallback((id) => {
    setData((current) => current && { ...current, products: current.products.filter((row) => row.id !== id) });
    refresh();
  }, [refresh]);

  const run = useCallback((key, request, onDone, failMessage) => {
    if (busyRef.current) return;
    busyRef.current = key;
    setBusy(key);
    request()
      .then((res) => {
        if (res.data.status === 200) onDone(res);
        else swal("Error", res.data.message || failMessage, "error");
      })
      .catch(() => swal("Error", failMessage, "error"))
      .finally(() => {
        busyRef.current = "";
        setBusy("");
      });
  }, []);

  const toggleFeatured = useCallback((row) => {
    run(`feat-${row.id}`, () => axios.put(`/api/toggle-product-featured/${row.id}`), (res) => {
      patchRow(row.id, { featured: Number(res.data.featured) === 1 || res.data.featured === true });
    }, "Could not update featured status");
  }, [run, patchRow]);

  const togglePopular = useCallback((row) => {
    run(`pop-${row.id}`, () => axios.put(`/api/toggle-product-popular/${row.id}`), (res) => {
      patchRow(row.id, { popular: Number(res.data.popular) === 1 || res.data.popular === true });
    }, "Could not update trending status");
  }, [run, patchRow]);

  const toggleOnStore = useCallback((row) => {
    const send = () => run(`store-${row.id}`, () => axios.put(`/api/product-on-store/${row.id}`, { on_store: row.on_store ? 0 : 1 }), (res) => {
      const status = Number(res.data.product_status);
      const visibility = res.data.store_visibility;
      patchRow(row.id, { on_store: status === 1 && (visibility === null || visibility === undefined || Number(visibility) === 1) });
    }, "Could not update the product");
    if (!row.on_store) {
      send();
      return;
    }
    swal({
      title: "Take this product off the store?",
      text: "Customers will not see it. The product, photos, and stock stay in your catalog.",
      icon: "warning",
      buttons: ["Cancel", "Take off store"],
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) send();
    });
  }, [run, patchRow]);

  const deleteProduct = useCallback((row) => {
    swal({
      title: "Delete this product?",
      text: `${row.name || "This product"} leaves the catalog. Past orders still show it. To keep it and hide it from customers, take it off the store instead.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      run(`delete-${row.id}`, () => axios.delete(`/api/delete-product/${row.id}`), (res) => {
        swal("Deleted", res.data.message || "Product removed from the catalog", "success");
        dropRow(row.id);
      }, "Could not delete the product");
    });
  }, [run, dropRow]);

  const restoreProduct = useCallback((row) => {
    run(`restore-${row.id}`, () => axios.put(`/api/restore-product/${row.id}`), (res) => {
      swal("Restored", res.data.message || `${row.name || "Product"} is back on the store`, "success");
      dropRow(row.id);
    }, "Could not restore the product");
  }, [run, dropRow]);

  // Exports every product that matches the current filters, page by page.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = [];
      let page = 1;
      let pageCount = 1;
      do {
        const res = await axios.get(listUrl(filtersToQuery({ ...filters, page, per_page: 100 })));
        rows.push(...(res.data.products || []));
        pageCount = res.data.meta?.page_count || 1;
        page += 1;
      } while (page <= pageCount);
      const header = ["ID", "Name", "Brand", "Category", "Price", "Cost", "Stock", "Featured", "Trending", "On store"];
      const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const lines = [header.join(",")].concat(rows.map((row) => [
        row.id, row.name, row.brand, row.category_name, row.selling_price, row.unit_cost, row.qty,
        row.featured ? "Yes" : "No", row.popular ? "Yes" : "No", row.on_store ? "Yes" : "No",
      ].map(escape).join(",")));
      const blob = new Blob([`﻿${lines.join("\n")}`], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      swal("Error", "The export could not be created.", "error");
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || { total: 0, in_stock: 0, low_stock: 0, out_of_stock: 0 };
  const categories = useMemo(() => data?.categories || [], [data]);
  const meta = data?.meta || { page: 1, page_count: 1, total: 0, per_page: filters.per_page };
  const rows = data?.products || [];
  const firstLoad = !data && fetching;
  const hasFilters = filters.q || filters.category !== "" || filters.stock || filters.status || filters.highlight;
  const from = meta.total ? (meta.page - 1) * meta.per_page + 1 : 0;
  const to = Math.min(meta.total, meta.page * meta.per_page);

  const sortHeader = (label, asc, desc) => {
    const on = filters.sort === asc || filters.sort === desc;
    const Icon = !on ? FaSort : (filters.sort === asc ? FaSortUp : FaSortDown);
    return (
      <button type="button" className={`ap-sort-btn ${on ? "is-on" : ""}`} onClick={() => setFilter("sort", filters.sort === desc ? asc : desc)}>
        {label} <Icon size={10} />
      </button>
    );
  };

  return (
    <div className="ap">
      <div className="ap-head">
        <div>
          <h4>
            {filters.deleted ? "Deleted products" : "Products"}
            {data ? <span className="ap-count">{stats.total}</span> : null}
          </h4>
          <p>
            {filters.deleted
              ? "These products are hidden from the catalog. Put one back on the store when you want to sell it again."
              : "Search, filter, and manage your catalog, stock, and what shows on the store."}
          </p>
        </div>
        <div className="ap-head-actions">
          <button
            type="button"
            className={`ap-btn ${filters.deleted ? "ap-btn-dark" : ""}`}
            onClick={() => {
              setSearchText("");
              setFilters({ ...DEFAULTS, per_page: filters.per_page, deleted: !filters.deleted });
            }}
          >
            {filters.deleted ? <FaChevronLeft size={11} /> : <FaTrash size={11} />}
            {filters.deleted ? "Back to products" : `Deleted${data?.deleted_count ? ` (${data.deleted_count})` : ""}`}
          </button>
          <button type="button" className="ap-btn" onClick={exportCsv} disabled={exporting || !meta.total}>
            {exporting ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <FaDownload size={11} />}
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          {!filters.deleted && (
            <Link to="/admin/add-product" className="ap-btn ap-btn-primary">
              <FaPlus size={11} /> Add product
            </Link>
          )}
        </div>
      </div>

      <div className="ap-stats">
        <StatTile icon={<FaBoxes />} tone="tone-blue" label="Total products" value={stats.total} loading={firstLoad}
          active={!filters.stock} onClick={() => setFilter("stock", "")} />
        <StatTile icon={<FaCheckCircle />} tone="tone-green" label="In stock" value={stats.in_stock} loading={firstLoad}
          active={filters.stock === "in_stock"} onClick={() => setFilter("stock", filters.stock === "in_stock" ? "" : "in_stock")} />
        <StatTile icon={<FaExclamationTriangle />} tone="tone-amber" label="Low stock" value={stats.low_stock} loading={firstLoad}
          note={variantNote(stats)}
          active={filters.stock === "low_stock"} onClick={() => setFilter("stock", filters.stock === "low_stock" ? "" : "low_stock")} />
        <StatTile icon={<FaTimesCircle />} tone="tone-red" label="Out of stock" value={stats.out_of_stock} loading={firstLoad}
          active={filters.stock === "out_of_stock"} onClick={() => setFilter("stock", filters.stock === "out_of_stock" ? "" : "out_of_stock")} />
        <StatTile icon={<FaTags />} tone="tone-violet" label="Categories" value={categories.length} loading={firstLoad} />
      </div>

      {categories.length > 0 && (
        <div className="ap-card ap-card-pad">
          <div className="ap-chips" role="toolbar" aria-label="Filter by category">
            <button type="button" className={`ap-chip ${filters.category === "" ? "is-active" : ""}`} onClick={() => setFilter("category", "")}>
              All <small>{stats.total}</small>
            </button>
            {categories.map((category) => (
              <button
                type="button"
                key={category.id}
                className={`ap-chip ${filters.category === String(category.id) ? "is-active" : ""}`}
                onClick={() => setFilter("category", filters.category === String(category.id) ? "" : String(category.id))}
              >
                {category.name} <small>{category.count}</small>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ap-card ap-card-pad">
        <div className="ap-toolbar">
          <div className="ap-search">
            <FaSearch size={13} />
            <input
              className="form-control"
              placeholder="Search name, brand, category, or #ID"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              aria-label="Search products"
            />
            {searchText ? (
              <button type="button" className="ap-search-clear" onClick={() => setSearchText("")} aria-label="Clear search"><FaTimes size={11} /></button>
            ) : null}
          </div>
          <select className="form-select" value={filters.category} onChange={(event) => setFilter("category", event.target.value)} aria-label="Category">
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={String(category.id)}>{category.name} ({category.count})</option>
            ))}
          </select>
          <select className="form-select" value={filters.stock} onChange={(event) => setFilter("stock", event.target.value)} aria-label="Stock">
            <option value="">Any stock</option>
            <option value="in_stock">In stock</option>
            <option value="low_stock">Low stock or options out</option>
            <option value="out_of_stock">Out of stock</option>
          </select>
          <select className="form-select" value={filters.status} onChange={(event) => setFilter("status", event.target.value)} aria-label="Store status">
            <option value="">Any status</option>
            <option value="on_store">On store</option>
            <option value="off_store">Off store</option>
          </select>
          <select className="form-select" value={filters.highlight} onChange={(event) => setFilter("highlight", event.target.value)} aria-label="Highlight">
            <option value="">All highlights</option>
            <option value="featured">Featured</option>
            <option value="popular">Trending</option>
          </select>
          <select className="form-select" value={filters.sort} onChange={(event) => setFilter("sort", event.target.value)} aria-label="Sort">
            {SORTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button type="button" className="ap-btn" onClick={resetFilters} disabled={!hasFilters} title="Reset filters">
            <FaRedo size={11} /> Reset
          </button>
        </div>
        <div className="ap-results">
          <span>
            {firstLoad ? "Loading products…" : (
              <>
                <strong>{meta.total}</strong> {meta.total === 1 ? "product" : "products"}
                {hasFilters ? (meta.total === 1 ? " matches these filters" : " match these filters") : ""}
              </>
            )}
          </span>
          {fetching && data ? <span className="d-inline-flex align-items-center gap-2"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Updating…</span> : null}
        </div>
      </div>

      <div className="ap-card" style={{ position: "relative" }}>
        {fetching && data ? <div className="ap-topbar" aria-hidden="true" /> : null}
        <div className="ap-table-wrap">
          <table className={`ap-table ${fetching && data ? "is-refreshing" : ""}`} aria-busy={fetching}>
            <thead>
              <tr>
                <th>{sortHeader("Product", "name_asc", "name_desc")}</th>
                <th>Category</th>
                <th>{sortHeader("Price & cost", "price_asc", "price_desc")}</th>
                <th>{sortHeader("Inventory", "stock_asc", "stock_desc")}</th>
                <th>Highlights</th>
                <th>Store</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {firstLoad ? (
                Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} />)
              ) : rows.length ? (
                rows.map((row) => (
                  <ProductRow
                    key={row.id}
                    row={row}
                    deleted={filters.deleted}
                    busy={busy.endsWith(`-${row.id}`) ? busy : ""}
                    locked={busy !== ""}
                    activeCategory={filters.category}
                    onCategory={filterCategory}
                    onFeatured={toggleFeatured}
                    onPopular={togglePopular}
                    onStore={toggleOnStore}
                    onDelete={deleteProduct}
                    onRestore={restoreProduct}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="ap-empty">
                      <div className="ap-empty-icon">{loadError ? <FaExclamationTriangle /> : <FaBoxes />}</div>
                      <h6>{loadError ? "Products could not be loaded" : (hasFilters ? "No products match" : (filters.deleted ? "No deleted products" : "No products yet"))}</h6>
                      <p className="mb-3">{loadError || (hasFilters ? "Try a different search or clear the filters." : (filters.deleted ? "Deleted products show up here." : "Add your first product to start selling."))}</p>
                      {loadError ? (
                        <button type="button" className="ap-btn" onClick={() => load()}><FaRedo size={11} /> Try again</button>
                      ) : hasFilters ? (
                        <button type="button" className="ap-btn" onClick={resetFilters}><FaRedo size={11} /> Clear filters</button>
                      ) : !filters.deleted ? (
                        <Link to="/admin/add-product" className="ap-btn ap-btn-primary"><FaPlus size={11} /> Add product</Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data && meta.total > 0 ? (
          <div className="ap-pager">
            <div className="ap-pager-left">
              <span>Showing <strong>{from}–{to}</strong> of <strong>{meta.total}</strong></span>
              <select className="form-select form-select-sm" value={filters.per_page} onChange={(event) => setFilter("per_page", Number(event.target.value))} aria-label="Rows per page">
                {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
              </select>
            </div>
            <Pages page={meta.page} pageCount={meta.page_count} onPage={goToPage} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// "4 options out · 1 low", or nothing when every option is stocked.
function variantNote(stats) {
  const parts = [];
  if (stats.variants_out) parts.push(`${stats.variants_out} ${stats.variants_out === 1 ? "option" : "options"} out`);
  if (stats.variants_low) parts.push(`${stats.variants_low} low`);
  return parts.join(" · ");
}

function StatTile({ icon, tone, label, value, loading, active, onClick, note }) {
  const body = (
    <>
      <span className={`ap-stat-icon ${tone}`}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        {loading ? <span className="ap-skel" style={{ width: 40, height: 22, marginBottom: 4 }} /> : <div className="ap-stat-value">{value}</div>}
        <div className="ap-stat-label">{label}</div>
        {note && !loading ? <div className="ap-stat-note">{note}</div> : null}
      </div>
    </>
  );
  if (!onClick) return <div className="ap-stat">{body}</div>;
  return (
    <button type="button" className={`ap-stat ${active ? "is-active" : ""}`} onClick={onClick} aria-pressed={!!active}>
      {body}
    </button>
  );
}

function Pages({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  const numbers = Array.from({ length: pageCount }, (_, index) => index + 1)
    .filter((number) => number === 1 || number === pageCount || Math.abs(number - page) <= 1);
  return (
    <nav className="ap-pages" aria-label="Product pages">
      <button type="button" className="ap-page" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><FaChevronLeft size={10} /></button>
      {numbers.map((number, index) => (
        <React.Fragment key={number}>
          {index > 0 && numbers[index - 1] !== number - 1 ? <span className="px-1">…</span> : null}
          <button
            type="button"
            className={`ap-page ${number === page ? "is-current" : ""}`}
            onClick={() => onPage(number)}
            aria-current={number === page ? "page" : undefined}
          >
            {number}
          </button>
        </React.Fragment>
      ))}
      <button type="button" className="ap-page" disabled={page >= pageCount} onClick={() => onPage(page + 1)} aria-label="Next page"><FaChevronRight size={10} /></button>
    </nav>
  );
}

function SkeletonRow() {
  return (
    <tr>
      <td>
        <div className="ap-product">
          <span className="ap-skel" style={{ width: 46, height: 46 }} />
          <div style={{ flex: 1 }}>
            <span className="ap-skel" style={{ width: "70%", height: 12, marginBottom: 6 }} />
            <span className="ap-skel" style={{ width: "40%", height: 10 }} />
          </div>
        </div>
      </td>
      {[80, 70, 90, 110, 70, 70].map((width, index) => (
        <td key={index}><span className="ap-skel" style={{ width, height: 14, marginLeft: index === 5 ? "auto" : 0 }} /></td>
      ))}
    </tr>
  );
}

// Shows the product total and, under it, how many sizes or colors are out or low.
function StockBadge({ row }) {
  const qty = Number(row.qty || 0);
  const out = Number(row.variants_out || 0);
  const low = Number(row.variants_low || 0);
  const status = row.stock_status || (qty <= 0 ? "out_of_stock" : qty <= 5 ? "low_stock" : "in_stock");
  const parts = [];
  if (out) parts.push(`${out} of ${row.variant_count} out`);
  if (low) parts.push(`${low} low`);
  return (
    <div>
      {status === "out_of_stock" ? (
        <span className="ap-badge ap-badge-red">Out of stock</span>
      ) : status === "low_stock" ? (
        <span className="ap-badge ap-badge-amber"><FaExclamationTriangle size={10} /> {qty} {qty <= 5 ? "left" : "in stock"}</span>
      ) : (
        <span className="ap-badge ap-badge-green"><FaCheckCircle size={10} /> {qty} in stock</span>
      )}
      {parts.length && status !== "out_of_stock" ? <div className="ap-variant-note">Options: {parts.join(", ")}</div> : null}
    </div>
  );
}

// Thumbnail that, on hover, shows a larger preview stepping through the
// product's photos. The preview is placed on the page, not in the table, so
// the table's scrolling never cuts it off.
function HoverThumb({ row }) {
  const photos = (Array.isArray(row.images) && row.images.length ? row.images : [row.image]).filter(Boolean);
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const start = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    setSpot({
      top: Math.max(8, Math.min(window.innerHeight - 212, box.top + box.height / 2 - 102)),
      left: box.right + 12,
    });
    photos.forEach((path) => {
      const preload = new Image();
      preload.src = getProductImageUrl(path);
    });
    if (photos.length > 1) {
      setIndex(1);
      clearInterval(timer.current);
      timer.current = setInterval(() => setIndex((current) => (current + 1) % photos.length), 900);
    }
  };
  const stop = () => {
    clearInterval(timer.current);
    setIndex(0);
    setSpot(null);
  };

  return (
    <span className="ap-thumb-wrap" onMouseEnter={start} onMouseLeave={stop}>
      <img
        className="ap-thumb"
        src={getProductImageUrl(photos[index] || row)}
        alt=""
        loading="lazy"
        decoding="async"
        width="46"
        height="46"
        onError={handleProductImageError}
      />
      {photos.length > 1 ? <span className="ap-thumb-count" title={`${photos.length} photos`}>{photos.length}</span> : null}
      {spot && photos.length ? (
        <span className="ap-thumb-pop" style={{ top: spot.top, left: spot.left }} aria-hidden="true">
          <img src={getProductImageUrl(photos[index])} alt="" onError={handleProductImageError} />
          {photos.length > 1 ? (
            <span className="ap-thumb-dots">
              {photos.map((path, dot) => <i key={path} className={dot === index ? "is-on" : ""} />)}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

const ProductRow = React.memo(function ProductRow({
  row, deleted, busy, locked, activeCategory, onCategory, onFeatured, onPopular, onStore, onDelete, onRestore,
}) {
  const price = Number(row.selling_price || 0);
  const cost = Number(row.unit_cost || 0);
  const margin = price > 0 && cost > 0 ? Math.round(((price - cost) / price) * 100) : null;
  const spinner = <span className="spinner-border spinner-border-sm" aria-hidden="true" />;

  return (
    <tr>
      <td>
        <div className="ap-product">
          <HoverThumb row={row} />
          <div style={{ minWidth: 0 }}>
            {deleted ? (
              <span className="ap-product-name" title={row.name}>{row.name}</span>
            ) : (
              <Link to={`/admin/edit-product/${row.id}`} className="ap-product-name" title={row.name}>{row.name}</Link>
            )}
            <div className="ap-product-meta">
              <span className="ap-id">#{row.id}</span>
              {row.brand ? ` · ${row.brand}` : ""}
            </div>
          </div>
        </div>
      </td>
      <td>
        <button
          type="button"
          className="ap-cat"
          style={activeCategory === String(row.category_id || 0) ? { borderColor: "var(--ap-accent)", color: "var(--ap-accent)" } : undefined}
          onClick={() => onCategory(row.category_id)}
          title={`Show only ${row.category_name}`}
        >
          {row.category_name}
        </button>
      </td>
      <td>
        <div className="ap-price">{money(price)}</div>
        <div className="ap-cost">
          {cost > 0 ? <>Cost {money(cost)}{margin !== null ? <> · <span className="ap-margin">{margin}%</span></> : null}</> : "No cost set"}
        </div>
      </td>
      <td>
        <StockBadge row={row} />
      </td>
      <td>
        <div className="d-flex gap-1">
          <button type="button" className={`ap-flag ${row.featured ? "is-feat" : ""}`} onClick={() => onFeatured(row)} disabled={locked || deleted}
            title={row.featured ? "Remove from featured" : "Mark as featured"} aria-pressed={row.featured}>
            {busy === `feat-${row.id}` ? spinner : <FaStar size={10} />} Featured
          </button>
          <button type="button" className={`ap-flag ${row.popular ? "is-pop" : ""}`} onClick={() => onPopular(row)} disabled={locked || deleted}
            title={row.popular ? "Remove from trending" : "Mark as trending"} aria-pressed={row.popular}>
            {busy === `pop-${row.id}` ? spinner : <FaFire size={10} />} Trending
          </button>
        </div>
      </td>
      <td>
        {deleted ? (
          <span className="ap-badge ap-badge-muted">Deleted</span>
        ) : (
          <button type="button" className={`ap-switch ${row.on_store ? "is-on" : ""}`} onClick={() => onStore(row)} disabled={locked}
            role="switch" aria-checked={row.on_store} title={row.on_store ? "Take off the store" : "Put on the store"}>
            {busy === `store-${row.id}` ? spinner : <span className="ap-switch-track" />}
            {row.on_store ? "On store" : "Off store"}
          </button>
        )}
      </td>
      <td>
        <div className="ap-actions">
          {deleted ? (
            <button type="button" className="ap-btn ap-btn-sm" onClick={() => onRestore(row)} disabled={locked}>
              {busy === `restore-${row.id}` ? spinner : <FaUndo size={11} />} Restore
            </button>
          ) : (
            <>
              <Link to={`/admin/edit-product/${row.id}`} className="ap-icon-btn" title="Edit" aria-label={`Edit ${row.name}`}><FaEdit size={13} /></Link>
              <button type="button" className="ap-icon-btn is-danger" onClick={() => onDelete(row)} disabled={locked} title="Delete" aria-label={`Delete ${row.name}`}>
                {busy === `delete-${row.id}` ? spinner : <FaTrash size={12} />}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
});
